import type { Color } from "@eu.mdotm.api/sphere";
import {
	colorMap,
	colors as defaultColors,
} from "$root/functional-areas/instruments/Instrument-customisation/SidePanelColumnsAdder";
import { preventSubmitOnPressEnter } from "$root/utils";
import {
	Banner,
	ClickableDiv,
	Column,
	DraggableList,
	FloatingContent,
	Icon,
	ScrollWrapper,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { TextInput, Button, IconButton, Label } from "@mdotm/shadcn/mdotm/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { ForEach } from "@mdotm/utils/react-extra";
import { useCallback, useMemo, useRef, useState } from "react";

// --- Types ---

export type TagOption = {
	option: string;
	color?: Color;
	/** Per-item override: when true, allows editing even if global allowEditOptionName is false */
	canEdit?: boolean;
};

type OptionStatus = "DUPLICATED" | "INVALID_FORMAT" | "VALID";

type InternalTagOption = TagOption & { _key: string };

// --- Props ---

/** Optional `data-qualifier` values for E2E targeting. Omitted attributes are simply not rendered. */
export type TagOptionsEditorQualifiers = {
	addInput?: string;
	addButton?: string;
	renameInput?: string;
	deleteButton?: (index: number, option: string | undefined) => string;
};

export type TagOptionsEditorProps = {
	value: TagOption[];
	onChange(options: TagOption[]): void;
	validationPattern?: RegExp;
	validationRules?: string[];
	/** When true, allows special characters (e.g. @, #, $, %) in option names. Ignored if `validationPattern` is provided. */
	allowSpecialCharacters?: boolean;
	colorPalette?: typeof defaultColors;
	qualifiers?: TagOptionsEditorQualifiers;
	allowAddOption: boolean;
	allowRemoveOption: boolean;
	/** Invoked instead of local removal when `allowRemoveOption` is true (e.g. to confirm/persist a deletion). */
	onRemoveOptionAsync?: (option: TagOption, index: number) => Promise<void>;
	allowEditOptionName: boolean;
	onRenameOption?: (oldName: string, newName: string) => Promise<void>;
	allowEditOptionOrder: boolean;
	allowEditOptionColor: boolean;
};

// --- Helpers ---

const DEFAULT_VALIDATION_PATTERN = /^[a-zA-Z0-9 ]{1,60}$/;
const DEFAULT_VALIDATION_RULES = [
	"1-60 characters long",
	"Allowed: Letters, numbers, spaces",
	"No special characters like @, #, $, %",
];

const SPECIAL_CHARACTERS_VALIDATION_PATTERN = /^.{1,60}$/;
const SPECIAL_CHARACTERS_VALIDATION_RULES = [
	"1-60 characters long",
	"Allowed: Letters, numbers, spaces, and special characters",
];

function stripInternalKey(option: InternalTagOption): TagOption {
	const { _key: _, ...rest } = option;
	return rest;
}

// --- Component ---

export function TagOptionsEditor(props: TagOptionsEditorProps) {
	const { value, onChange, validationPattern, validationRules, allowSpecialCharacters, colorPalette } = props;

	const pattern =
		validationPattern ?? (allowSpecialCharacters ? SPECIAL_CHARACTERS_VALIDATION_PATTERN : DEFAULT_VALIDATION_PATTERN);
	const rules =
		validationRules ?? (allowSpecialCharacters ? SPECIAL_CHARACTERS_VALIDATION_RULES : DEFAULT_VALIDATION_RULES);
	const colors = colorPalette ?? defaultColors;

	const [searchText, setSearchText] = useState("");
	const [optionStatus, setOptionStatus] = useState<OptionStatus>("VALID");
	const [colorIndex, setColorIndex] = useState(0);

	// Stable internal keys for DraggableList
	const keyCounterRef = useRef(0);
	const keyMapRef = useRef(new Map<string, string>());
	const internalOptions = useMemo<InternalTagOption[]>(() => {
		const keyMap = keyMapRef.current;
		return value.map((opt) => {
			const stableKey = opt.option ?? "";
			if (!keyMap.has(stableKey)) {
				keyCounterRef.current += 1;
				keyMap.set(stableKey, `tag-opt-${keyCounterRef.current}`);
			}
			return { ...opt, _key: keyMap.get(stableKey)! };
		});
	}, [value]);

	const resetSearch = useCallback(() => {
		setSearchText("");
		setOptionStatus("VALID");
	}, []);

	const filteredOptions = useMemo(
		() =>
			internalOptions.filter(({ option }) => {
				const textLower = option?.toLowerCase();
				const words = searchText.toLowerCase().split(" ");
				return words.every((word) => textLower?.includes(word));
			}),
		[internalOptions, searchText],
	);

	const optionNameExist = useCallback(
		(name: string) => value.some(({ option }) => option?.toLowerCase() === name.toLowerCase()),
		[value],
	);

	const validateOptionStatusOnChange = useCallback(
		(newText: string) => {
			if (optionStatus === "INVALID_FORMAT") {
				if (pattern.test(newText)) {
					setOptionStatus("VALID");
				}
				return;
			}
			if (optionStatus === "DUPLICATED") {
				if (!optionNameExist(newText)) {
					setOptionStatus("VALID");
				}
			}
		},
		[optionStatus, optionNameExist, pattern],
	);

	const insertTypedOption = useCallback(() => {
		if (optionNameExist(searchText)) {
			setOptionStatus("DUPLICATED");
			return;
		}

		if (!pattern.test(searchText)) {
			setOptionStatus("INVALID_FORMAT");
			return;
		}

		const newOption: TagOption = {
			color: colors[colorIndex].key,
			option: searchText,
			canEdit: true,
		};

		onChange([newOption, ...value]);

		setColorIndex((index) => (index + 1 > colors.length - 1 ? 0 : index + 1));
		resetSearch();
	}, [optionNameExist, searchText, pattern, colors, colorIndex, onChange, value, resetSearch]);

	// --- Internal mutation helpers ---

	const handleReorder = useCallback(
		(reordered: InternalTagOption[]) => {
			onChange(reordered.map(stripInternalKey));
		},
		[onChange],
	);

	const handleUpdate = useCallback(
		(index: number, updated: TagOption) => {
			onChange(value.map((x, i) => (i === index ? updated : x)));
		},
		[onChange, value],
	);

	const handleRemove = useCallback(
		(index: number) => {
			onChange(value.filter((_, i) => i !== index));
		},
		[onChange, value],
	);

	return (
		<>
			<Label htmlFor="" className="mb-px">
				Add options
			</Label>
			<div className="flex space-x-2">
				<TextInput
					size="small"
					disabled={!props.allowAddOption}
					value={searchText}
					onChangeText={(newText) => {
						validateOptionStatusOnChange(newText);
						setSearchText(newText);
					}}
					className={{ "mb-2 flex-1": true }}
					inputAppearance={{
						className: {
							[`!border-[color:var(--palette-S200)] animate-tremble`]: optionStatus !== "VALID",
						},
					}}
					placeholder="Type a new option..."
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							insertTypedOption();
						}
					}}
					onKeyDownCapture={preventSubmitOnPressEnter}
					rightContent={
						searchText ? (
							<IconButton
								size={16}
								icon="Cancel"
								onClick={() => resetSearch()}
								hoverBackground=""
								className="p-0!"
								color={themeCSSVars.palette_N400}
							/>
						) : undefined
					}
					data-qualifier={props.qualifiers?.addInput}
				/>
				<Button
					palette="secondary"
					size="small"
					onClick={insertTypedOption}
					disabled={optionStatus !== "VALID" || !props.allowAddOption}
					data-qualifier={props.qualifiers?.addButton}
				>
					<Icon icon="Outline1" />
				</Button>
			</div>
			<OptionStatusBanner status={optionStatus} rules={rules} />
			<div className="flex-1 min-h-0 overflow-auto scroll-smooth">
				<DraggableList
					itemKey={(field) => field._key}
					items={filteredOptions}
					onReorder={handleReorder}
					disabled={!props.allowEditOptionOrder}
				>
					{({ index, item, isDragging, draggableProps, innerRef, dragHandleProps }) => (
						<div
							style={{ borderColor: themeCSSVars.palette_N50 }}
							className={toClassName({
								"relative flex flex-row items-center min-w-0 bg-white gap-2 min-h-12 h-12 transition-all": true,
								"px-2": !searchText,
								"shadow-lg": isDragging,
							})}
							{...draggableProps}
							ref={innerRef}
						>
							<div
								{...dragHandleProps}
								className={toClassName({
									invisible: !draggableProps || !dragHandleProps,
									hidden: Boolean(searchText) || !props.allowEditOptionOrder,
								})}
							>
								<DragHandleIcon />
							</div>
							<ClickableOptionPopover
								allowEditOptionName={props.allowEditOptionName}
								allowEditOptionColor={props.allowEditOptionColor}
								item={item}
								searchText={searchText}
								colors={colors}
								validationPattern={pattern}
								renameInputQualifier={props.qualifiers?.renameInput}
								onClose={(params) => {
									if (!params.newOption.option) {
										return "INVALID_FORMAT";
									}

									if (!pattern.test(params.newOption.option)) {
										return "INVALID_FORMAT";
									}

									const exist = optionNameExist(params.newOption.option);
									if (exist && item.option !== params.newOption.option) {
										return "DUPLICATED";
									}

									if (props.allowEditOptionName && props.onRenameOption && item.option !== params.newOption.option) {
										// The popover closes on the gesture, ahead of the rename it starts. Left open it is still armed for
										// click-away, and a confirmation the callback raises is a click outside it — which starts the same
										// rename a second time the moment the user answers it.
										params.close();

										// The callback owns the rename, so the row waits for it rather than showing the new name and
										// hoping. A rejection is a veto — the user declined the confirmation, or the server refused the
										// name — and the row must still read as it did, colour included.
										props.onRenameOption(item.option, params.newOption.option).then(
											() => handleUpdate(index, stripInternalKey(params.newOption)),
											() => params.revert(),
										);
										return "VALID";
									}

									handleUpdate(index, stripInternalKey(params.newOption));
									params.close();
									return "VALID";
								}}
							/>
							{(props.allowRemoveOption || item.canEdit) && (
								<IconButton
									icon="Delete"
									onClickAsync={async () => {
										if (props.allowRemoveOption && props.onRemoveOptionAsync) {
											await props.onRemoveOptionAsync(stripInternalKey(item), index);
											return;
										}
										handleRemove(index);
									}}
									className="ml-auto"
									color={themeCSSVars.palette_P400}
									data-qualifier={props.qualifiers?.deleteButton?.(index, item.option)}
								/>
							)}
						</div>
					)}
				</DraggableList>
			</div>
		</>
	);
}

// --- Sub-components ---

function OptionStatusBanner({ status, rules }: { status: OptionStatus; rules?: string[] }) {
	const displayRules = rules ?? DEFAULT_VALIDATION_RULES;
	return status === "DUPLICATED" ? (
		<Banner severity="info" className="mb-2">
			Option already exist
		</Banner>
	) : status === "INVALID_FORMAT" ? (
		<Banner severity="info" className="mb-2" title="Please follow these rules for valid name entry">
			<ul style={{ listStyleType: "disc" }}>
				<ForEach collection={displayRules}>{(item) => <li>{item}</li>}</ForEach>
			</ul>
		</Banner>
	) : (
		<></>
	);
}

function ClickableOptionPopover({
	item,
	searchText,
	allowEditOptionName,
	allowEditOptionColor,
	colors,
	validationPattern,
	renameInputQualifier,
	onClose,
}: {
	item: InternalTagOption;
	searchText: string;
	allowEditOptionName: boolean;
	allowEditOptionColor: boolean;
	colors: typeof defaultColors;
	validationPattern: RegExp;
	renameInputQualifier?: string;
	onClose(params: { newOption: InternalTagOption; close(): void; revert(): void }): OptionStatus;
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [internalOption, setInternalOption] = useState(item);
	const [optionStatus, setOptionStatus] = useState<OptionStatus>("VALID");

	return (
		<FloatingContent
			position="bottom"
			align="startToStart"
			triggerLocation="behind"
			open={isOpen}
			// Click-away stays armed briefly after close while the exit transition settles; a closed popover has
			// nothing left to commit.
			onClickAway={() =>
				isOpen &&
				setOptionStatus(
					onClose({
						newOption: internalOption,
						close() {
							setIsOpen(false);
						},
						revert() {
							setInternalOption(item);
						},
					}),
				)
			}
			trigger={({ innerRef }) => (
				<ClickableDiv
					innerRef={innerRef}
					onClick={() => setIsOpen(true)}
					className={{
						"px-2 py-1.5 rounded-sm": true,
						"ml-2": !searchText,
					}}
					style={{ backgroundColor: item.color ? colorMap[item.color].badgeColor() : colorMap.GRAY.badgeColor() }}
				>
					<Text type="Body/M/Medium">{item.option}</Text>
				</ClickableDiv>
			)}
			relativePositionOffsets={{ bottom: -8, top: 8 }}
			relativeAlignmentOffsets={{ startToStart: -8 }}
		>
			{({ triggerBox, position }) => (
				<Column
					minSize="0"
					className={{
						"pt-2 pb-2 px-2 shadow-[0px_8px_32px_rgba(0,0,0,0.16)] rounded-sm bg-white flex flex-col flex-1 overflow-hidden": true,
					}}
					style={{
						minHeight: triggerBox.height,
						minWidth: triggerBox.width,
						maxHeight: 270,
						width: 225,
					}}
					gap={8}
					reverse={position === "top"}
				>
					<TextInput
						disabled={!allowEditOptionName && !item.canEdit}
						size="small"
						value={internalOption.option ?? ""}
						onChangeText={(name) => setInternalOption((latestOption) => ({ ...latestOption, option: name }))}
						className={{
							"flex-1": true,
							"border-(--palette-S200)! animate-tremble": optionStatus !== "VALID",
						}}
						placeholder="Type a new option..."
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								setOptionStatus(
									onClose({
										newOption: internalOption,
										close() {
											setIsOpen(false);
										},
										revert() {
											setInternalOption(item);
										},
									}),
								);
							}
						}}
						rightContent={
							internalOption.option !== item.option ? (
								<IconButton
									size={14}
									icon="restore"
									onClick={() => setInternalOption((latestOption) => ({ ...latestOption, option: item.option }))}
									hoverBackground=""
									className="p-0!"
									color={themeCSSVars.palette_N400}
								/>
							) : undefined
						}
						data-qualifier={renameInputQualifier}
					/>
					<OptionStatusBanner status={optionStatus} />
					<div className="min-h-0 flex-1 flex flex-col -mx-2">
						<ScrollWrapper direction="vertical" outerContainerAppearance={{ className: "flex-1" }}>
							<ForEach collection={colors}>
								{({ badgeColor, label, key }) => (
									<ClickableDiv
										disabled={!allowEditOptionColor}
										className="flex space-x-2 px-2 py-2 items-center hover:bg-(--palette-P50)"
										onClick={() => setInternalOption((latestOption) => ({ ...latestOption, color: key }))}
									>
										<div
											className="rounded-sm h-6 w-6 flex items-center justify-center"
											style={{ backgroundColor: badgeColor() }}
										>
											{internalOption.color === key && (
												<Icon icon="Outline" size={14} color={themeCSSVars.palette_N600} />
											)}
										</div>
										<Text type="Body/M/Medium">{label}</Text>
									</ClickableDiv>
								)}
							</ForEach>
						</ScrollWrapper>
					</div>
				</Column>
			)}
		</FloatingContent>
	);
}

function DragHandleIcon() {
	return (
		<svg role="presentation" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M6.04181 3.2944L7.96096 1.37506C8.01677 1.31944 8.10704 1.31944 8.16286 1.37506L10.082 3.2944C10.1228 3.3352 10.1349 3.3966 10.113 3.44991C10.0909 3.50323 10.0389 3.53807 9.98115 3.53807H6.14267C6.08493 3.53807 6.03277 3.50323 6.01083 3.44991C5.98888 3.3966 6.00101 3.3352 6.04181 3.2944ZM8.16305 14.8297C8.10724 14.8853 8.01697 14.8853 7.96115 14.8297L6.04181 12.9103C6.00101 12.8695 5.98888 12.8081 6.01083 12.7548C6.03296 12.7015 6.08512 12.6667 6.14286 12.6667H9.98134C10.0391 12.6667 10.091 12.7015 10.1132 12.7548C10.1351 12.8081 10.123 12.8695 10.0822 12.9103L8.16305 14.8297ZM14.3446 6.00001H1.97822C1.62186 6.00001 1.33337 6.29415 1.33337 6.65052C1.33337 7.00688 1.33337 7.30102 1.97822 7.30102H14.3333C14.701 7.30102 14.9895 7.01254 14.9895 6.65052C14.9895 6.28849 14.701 6.00001 14.3446 6.00001ZM1.97822 9.33334H14.3446C14.701 9.33334 14.9895 9.62183 14.9895 9.98385C14.9895 10.3459 14.701 10.6344 14.3333 10.6344H1.97822C1.33337 10.6344 1.33337 10.3402 1.33337 9.98385C1.33337 9.62748 1.62186 9.33334 1.97822 9.33334Z"
				fill="#8792AB"
			/>
		</svg>
	);
}
